import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { ThemeProvider } from './contexts/ThemeContext'
import { I18nextProvider } from 'react-i18next'
import { DataProvider } from './contexts/DataContext'
import i18n from './i18n'
import Header from './components/Header'
import HomeScreen from './screens/HomeScreen'
import LoginScreen from './screens/LoginScreen'
import VideoDetailScreen from './screens/VideoDetailScreen'
import ProfileScreen from './screens/ProfileScreen'
import AdminScreen from './screens/AdminScreen'
import WatchHistoryScreen from './screens/WatchHistoryScreen'
import PlaylistsScreen from './screens/PlaylistsScreen'
import './App.css'

interface User { id: string; email: string; username: string; role: 'admin' | 'user'; created_at: string }

// ── Clear stale caches on every app load ──────────────────────────────────────
const clearStaleCaches = async () => {
  try {
    // 1. Unregister any lingering service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(r => r.unregister()))
    }
    // 2. Clear all Cache API entries
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k)))
    }
  } catch { /* cache clearing is best-effort */ }
}

// ── Log session for web visitors ──────────────────────────────────────────────
const logWebSession = async (userId: string | null) => {
  try {
    let ipAddress = 'unknown', country = 'unknown'
    try {
      const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) })
      const data = await res.json()
      ipAddress = data.ip || 'unknown'
      country = data.country_name || data.country || 'unknown'
    } catch (_) { /* ip fetch failed — use defaults */ }
    await supabase.from('app_sessions').insert({
      user_id: userId, ip_address: ipAddress, country,
      device_info: navigator.userAgent.slice(0, 100),
      platform: 'web', opened_at: new Date().toISOString(), is_guest: !userId,
    })
  } catch (_) { /* session logging is non-critical */ }
}

function AppContent() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const sessionLogged = React.useRef(false)
  const loadingDone   = React.useRef(false)

  const finishLoading = React.useCallback(() => {
    if (loadingDone.current) return
    loadingDone.current = true
    setLoading(false)
  }, [])

  useEffect(() => {
    // Clear stale caches immediately
    clearStaleCaches()

    // Hard timeout — never stay on loading screen more than 3 seconds
    const timeout = setTimeout(() => {
      console.warn('Auth timeout — showing app')
      finishLoading()
    }, 2000)

    // Race getSession against a 1.5s timeout — whichever wins first
    const sessionRace = Promise.race([
      supabase.auth.getSession(),
      new Promise<{ data: { session: null } }>(res =>
        setTimeout(() => res({ data: { session: null } }), 1500)
      )
    ])

    sessionRace.then(({ data: { session } }) => {
      clearTimeout(timeout)
      if (session?.user) {
        fetchUserProfile(session.user.id)
      } else {
        finishLoading()
        if (!sessionLogged.current) { sessionLogged.current = true; logWebSession(null) }
      }
    }).catch(() => {
      clearTimeout(timeout)
      finishLoading()
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        await fetchUserProfile(session.user.id)
        if (event === 'SIGNED_IN' && !sessionLogged.current) {
          sessionLogged.current = true; logWebSession(session.user.id)
        }
      } else {
        setUser(null)
        finishLoading()
      }
    })

    return () => { subscription.unsubscribe(); clearTimeout(timeout) }
  }, [])

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).single()
      if (data) setUser(data)
    } catch (_) { /* ip fetch failed — use defaults */ } finally { finishLoading() }
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
      <div style={{ width: 72, height: 72, borderRadius: 22, background: 'linear-gradient(135deg,#e53935,#8e24aa,#1565c0)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 40px rgba(229,57,53,0.4)' }}>
        <span style={{ color: '#fff', fontSize: 30, marginLeft: 4 }}>▶</span>
      </div>
      <div style={{ width: 36, height: 36, border: '3px solid #e53935', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ color: '#444', fontSize: 12, marginTop: 8 }}>Loading…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )

  return (
    <Router>
      <CacheInvalidator />
      <Header user={user} />
      <main>
        <Routes>
          <Route path="/" element={<HomeScreen currentUser={user} />} />
          <Route path="/video/:id" element={<VideoDetailScreen currentUser={user} />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/profile" element={user ? <ProfileScreen currentUser={user} /> : <Navigate to="/login" replace />} />
          <Route path="/admin" element={user?.role === 'admin' ? <AdminScreen currentUser={user} /> : <Navigate to="/" replace />} />
          <Route path="/history" element={user ? <WatchHistoryScreen currentUser={user} /> : <Navigate to="/login" replace />} />
          <Route path="/playlists" element={user ? <PlaylistsScreen currentUser={user} /> : <Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </Router>
  )
}

// ── Clears caches on every route change ───────────────────────────────────────
function CacheInvalidator() {
  useEffect(() => {
    // Force browser to reload JS/CSS from network on next navigation
    // by busting any in-memory fetch cache for non-asset requests
    if ('caches' in window) {
      caches.keys().then(keys =>
        Promise.all(
          keys
            .filter(k => !k.includes('assets')) // keep hashed JS/CSS
            .map(k => caches.delete(k))
        )
      ).catch(() => {})
    }
  })
  return null
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <DataProvider>
          <AppContent />
        </DataProvider>
      </ThemeProvider>
    </I18nextProvider>
  )
}

export default App